import { useState, useEffect } from "react";
import noImage from "../../assets/images/no-image.png";
import imageLoadingErr1 from "../../assets/images/image-error.png";

/**
 * A component that loads an image from a URL and displays it.
 * If the image fails to load, a fallback image is displayed.
 *
 * NOTE: ALL PROPS ARE THE SAME AS THE NATIVE IMG TAG.
 *
 * @component
 * @param {Object} props - NOTE: ALL PROPS ARE THE SAME AS THE NATIVE IMG TAG.
 * @param {string} props.src - The URL of the image to load.
 * @param {string} [props.alt="Image"] - The alt text for the image.
 * @param {string} [props.imageError] - The URL of the image to display when the image fails to load.
 * @param {string} [props.noImage] - The URL of the image to display when the image URL is empty.
 * @param {string} [props.imageLoading] - The URL of the image to display while the image is loading.
 * @param {boolean} [props.hideOnError] - Indicates if the image should be hidden when it fails to load.
 * @param {boolean} [props.hideOnNoImage] - Indicates if the image should be hidden when the image URL is empty.
 * @returns {JSX.Element} The rendered ImageLoadingUrl component.
 */
export default function ImageLoadingUrl({
    src,
    alt = "Image",
    imageError,
    noImage: propNoImage,
    imageLoading,
    hideOnError,
    hideOnNoImage,
    style,
    ...props
}) {
    // #region VARS
    const [loading, setLoading] = useState(true);
    const [currentImageSrc, setCurrentImageSrc] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [isHidden, setIsHidden] = useState(false);

    const imgLoading = imageLoading ?? SimpleImageLoading;
    const imgError = imageError ?? imageLoadingErr1;
    const imgNoImage = propNoImage ?? noImage;
    // #endregion

    // #region METHODS
    const validateImageSrc = url => {
        if ([null, undefined, "", "undefined", "-", "--"].includes(url)) {
            return false;
        }
        return true;
    };

    const getRelevantImgProps = () => {
        const imgProps = {};
        for (const key in props) {
            if (key === "hideOnError" || key === "hideOnNoImage") continue;
            imgProps[key] = props[key];
        }

        return imgProps;
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setHasError(false);
        setIsHidden(false);

        if (!validateImageSrc(src)) {
            setCurrentImageSrc(imgNoImage);
            setHasError(true);
            setLoading(false);
            if (hideOnNoImage) setIsHidden(true);
            return;
        }

        // Use new Image() to preload and check validity without CORS fetch limitations
        const img = new Image();
        img.src = src;

        img.onload = () => {
            if (isMounted) {
                setCurrentImageSrc(src);
                setLoading(false);
            }
        };

        img.onerror = () => {
            if (isMounted) {
                setCurrentImageSrc(imgError);
                setHasError(true);
                setLoading(false);
                if (hideOnError) setIsHidden(true);
            }
        };

        return () => {
            isMounted = false;
            img.onload = null;
            img.onerror = null;
        };
    }, [src, hideOnNoImage, hideOnError, imgNoImage, imgError]);
    // #endregion

    // #region RENDER
    return (
        <img
            {...getRelevantImgProps()}
            alt={alt}
            src={loading ? imgLoading : currentImageSrc}
            style={{
                ...style,
                objectFit: loading ? "none" : hasError ? "contain" : style?.objectFit,
                padding: hasError ? "0px" : style?.padding,
                display: isHidden ? "none" : style?.display
            }}
        />
    );
    // #endregion
}

const SimpleImageLoading =
    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MCA1MCIgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIj4KICA8Y2lyY2xlIGN4PSIyNSIgY3k9IjI1IiByPSIyMCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZDFkMWQxIiBzdHJva2Utd2lkdGg9IjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCI+CiAgICA8YW5pbWF0ZSBhdHRyaWJ1dGVOYW1lPSJzdHJva2UtZGFzaGFycmF5IiBkdXI9IjEuNXMiIHJlcGVhdENvdW50PSJpbmRlZmluaXRlIiB2YWx1ZXM9IjEsMjAwOzkwLDE1MDsxLDIwMCIvPgogICAgPGFuaW1hdGUgYXR0cmlidXRlTmFtZT0ic3Ryb2tlLWRhc2hvZmZzZXQiIGR1cj0iMS41cyIgcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiIHZhbHVlcz0iMDstMzU7LTEyNCIvPgogICAgPGFuaW1hdGVUcmFuc2Zvcm0gYXR0cmlidXRlTmFtZT0idHJhbnNmb3JtIiB0eXBlPSJyb3RhdGUiIGZyb209IjAgMjUgMjUiIHRvPSIzNjAgMjUgMjUiIGR1cj0iMS41cyIgcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiLz4KICA8L2NpcmNsZT4KPC9zdmc+";
